import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { kebabCase } from "lodash";
import Typography from "@shared/v2/Typography";
import Dropdown from "@shared/v2/Dropdown";
import TextInput from "@shared/v2/TextInput";
import FieldLabel from "@shared/v2/FieldLabel";
import IconButton from "@shared/v2/IconButton";
import Tooltip from "@shared/v2/Tooltip";
import { CircleInfoSolidV6, TrashCanSolidV6 } from "@shared/v2/Icomoon";
import { useDebounce } from "@shared/hookHelpers";
import { useDispatch } from "react-redux";
import { getAreaAutoComplete, setAreaSelection } from "../../reducers/websiteReducer/areaPageReducer";
import { useMlsIds } from "../../reducers/websiteReducer/areaPageSelectors";
import { AREA_GROUPS, AREA_SEARCH_MIN_CHARS } from "./areaSearch";

// First step of the area-page create flow: pick an MLS area (required), set a title, and
// preview the slug. Areas come from the GeoAddressService area search.
const PageDetailsStep = ({ onAreaChanged, onTitleChanged, onSlugChanged }) => {
  const [areaGroups, setAreaGroups] = useState([]);
  const [area, setArea] = useState(null);
  const [title, setTitle] = useState("");
  const [titleEdited, setTitleEdited] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 250);
  const dispatch = useDispatch();
  const mlsIds = useMlsIds();

  useEffect(() => {
    let stale = false;
    if (debouncedSearchTerm.length >= AREA_SEARCH_MIN_CHARS) {
      const data = {
        search_term: debouncedSearchTerm,
        search_type: "fsbos",
        mls_ids: mlsIds,
      };

      dispatch(getAreaAutoComplete(data)).then((res) => {
        if (stale) return;
        const results = res.payload?.data?.results || {};
        const result = AREA_GROUPS.filter(([key]) => Array.isArray(results[key]) && results[key].length).map(
          ([key, label]) => ({
            label,
            options: results[key].map((item) => ({
              label: item.name,
              value: item.name,
              lat: item.latitude,
              lng: item.longitude,
              type: key,
            })),
          }),
        );

        setAreaGroups(result);
      });
    } else {
      setAreaGroups([]);
    }
    return () => {
      stale = true;
    };
  }, [debouncedSearchTerm]);

  const slug = useMemo(() => (title.trim() ? `${kebabCase(title.replace(/[^\w\s-]+/g, ""))}` : ""), [title]);

  const onAreaInputChanged = (e) => {
    setSearchTerm(e);
  };

  useEffect(() => {
    onTitleChanged(title);
    onSlugChanged(slug);
  }, [title]);

  const onAreaSelected = (option) => {
    setArea(option);
    onAreaChanged(option);
    // Auto-fill the title from the area name, unless the user already typed their own.
    if (option && !titleEdited) {
      setTitle(option.label);
    }

    dispatch(setAreaSelection(option));
  };

  const changeTitle = (e) => {
    setTitle(e.target.value);
    setTitleEdited(true);
    onTitleChanged(e.target.value);
    onSlugChanged(slug);
  };

  const error = title.trim() ? "" : "This field is required";

  return (
    <div className="tw-max-w-[800px] tw-w-full tw-mx-auto tw-my-[16px] tw-flex tw-flex-col tw-gap-[24px] tw-bg-white tw-border-[1px] tw-border-solid tw-border-gray-10 tw-rounded-[8px] tw-p-[32px]">
      <div className="tw-flex tw-flex-col tw-gap-[8px]">
        <Typography variant="headline3">Page Details</Typography>
        <Typography variant="body-m" className="!tw-text-gray-50">
          Select an area you want to feature and set up some basic information to get you started. You will be
          able to edit your title and slug later. The area <strong>cannot</strong> be updated after this step.
        </Typography>
      </div>

      <div className="tw-flex tw-flex-col tw-gap-[4px]">
        <div className="tw-flex tw-items-end tw-gap-[8px]">
          <Dropdown
            containerClassName="tw-w-full"
            label={area ? "Select an MLS Area" : "Select Area in Your MLS"}
            isRequired
            error={area ? "" : "This field is required"}
            placeholder="Search by city, county, ZIP/Postal, neighborhood, or school"
            menuShouldComeToFront
            isSearchable
            options={areaGroups}
            value={area}
            onChange={onAreaSelected}
            onInputChange={onAreaInputChanged}
          />
          {area && (
            <IconButton
              schema="tertiary"
              onClick={() => {
                // Clear every holder of the selection: local state, the parent's page
                // payload, and the redux copy the editor seeds its panels from. The
                // title goes too when it was auto-filled from the area (never when the
                // user typed their own).
                setArea(null);
                onAreaChanged("");
                dispatch(setAreaSelection(null));
                if (!titleEdited) setTitle("");
              }}
            >
              <TrashCanSolidV6 />
            </IconButton>
          )}
        </div>
      </div>

      <TextInput
        label="Page Title"
        isRequired
        maxLength={256}
        placeholder="Enter a title for your page"
        value={title}
        onChange={changeTitle}
        helperText={error ? "" : `${title.trim().length} / 256 characters`}
        error={error}
      />

      <div className="tw-flex tw-flex-col tw-gap-[8px]">
        <FieldLabel
          label={
            <>
              Slug{" "}
              <Tooltip
                innerClassName="tw-max-w-[220px]"
                trigger={<CircleInfoSolidV6 />}
                content="Site visitors will be able to see your page at this URL after publishing."
              />
            </>
          }
        />
        <Typography variant="body-m" className={slug ? "" : "!tw-text-gray-50"}>
          {slug ? `/${slug}` : "Enter a title to view the page slug"}
        </Typography>
      </div>
    </div>
  );
};

PageDetailsStep.propTypes = {
  onAreaChanged: PropTypes.func.isRequired,
  onTitleChanged: PropTypes.func.isRequired,
  onSlugChanged: PropTypes.func.isRequired,
};

export default PageDetailsStep;
